import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Calendar, Clock, Users, Euro, Heart, MessageCircle, User, ArrowLeft, Send } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const getLevelClass = (level) => {
  switch (level?.toLowerCase()) {
    case 'anfänger': return 'level-anfanger';
    case 'mittelstufe': return 'level-mittelstufe';
    case 'fortgeschritten': return 'level-fortgeschritten';
    default: return 'level-anfanger';
  }
};

export const CourseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [course, setCourse] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingType, setBookingType] = useState('single');
  const [partnerEmail, setPartnerEmail] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isPartnerSearch, setIsPartnerSearch] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);

  useEffect(() => {
    fetchCourse();
    fetchComments();
  }, [id]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/api/courses.php?action=get&id=${id}`);
      setCourse(response.data);
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Kurs nicht gefunden');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axios.get(`${API}/api/comments.php?action=list&course_id=${id}`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleBooking = async () => {
    if (!user) {
      toast.error('Bitte melden Sie sich an, um zu buchen');
      navigate('/login', { state: { from: { pathname: `/courses/${id}` } } });
      return;
    }

    if (bookingType === 'couple' && !partnerEmail) {
      toast.error('Bitte geben Sie die E-Mail Ihres Partners ein');
      return;
    }

    setBookingLoading(true);
    try {
      const response = await axios.post(`${API}/api/checkout.php?action=create`, {
        course_id: id,
        booking_type: bookingType,
        partner_email: bookingType === 'couple' ? partnerEmail : null,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Buchung fehlgeschlagen');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Bitte melden Sie sich an, um zu kommentieren');
      navigate('/login', { state: { from: { pathname: `/courses/${id}` } } });
      return;
    }

    if (!newComment.trim()) return;

    setCommentLoading(true);
    try {
      await axios.post(`${API}/api/comments.php?action=create`, {
        course_id: id,
        content: newComment,
        is_partner_search: isPartnerSearch
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(isPartnerSearch ? 'Partnersuche veröffentlicht!' : 'Kommentar hinzugefügt!');
      setNewComment('');
      setIsPartnerSearch(false);
      fetchComments();
    } catch (error) {
      toast.error('Kommentar konnte nicht hinzugefügt werden');
    } finally {
      setCommentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!course) return null;

  const spotsLeft = course.capacity - course.enrolled;
  const isFull = spotsLeft <= 0;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="pt-32 pb-16 px-6" data-testid="course-detail-page">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <button 
            onClick={() => navigate('/courses')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zu allen Kursen
          </button>

          {/* Course Header */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-2 animate-fadeIn">
              <div className="flex items-center gap-4 mb-4">
                <Badge className={`level-badge ${getLevelClass(course.level)}`}>
                  {course.level}
                </Badge>
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                {course.name}
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {course.description}
              </p>
            </div>

            {/* Booking Card */}
            <div className="bg-card border border-border rounded-md p-6 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
              <div className="text-3xl font-heading font-bold text-accent mb-6">
                €{course.price.toFixed(2)}
                <span className="text-sm text-muted-foreground font-normal ml-2">/ Person</span>
              </div>

              <div className="space-y-4 mb-6 text-sm">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span>Start: {new Date(course.start_date).toLocaleDateString('de-DE', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Clock className="w-5 h-5 text-primary" />
                  <span>{course.schedule}</span>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Users className="w-5 h-5 text-primary" />
                  {isFull ? (
                    <span className="text-destructive font-medium">Ausgebucht</span>
                  ) : (
                    <span>{spotsLeft} von {course.capacity} Plätzen frei</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Euro className="w-5 h-5 text-primary" />
                  <span>{course.duration_weeks} Wochen</span>
                </div>
              </div>

              <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="w-full btn-primary" 
                    disabled={isFull}
                    data-testid="book-btn"
                  >
                    {isFull ? 'Ausgebucht' : 'Jetzt buchen'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">Buchung: {course.name}</DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-6 py-4">
                    <div className="space-y-3">
                      <Label className="form-label">Buchungsart</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          type="button"
                          onClick={() => setBookingType('single')}
                          className={`p-4 border rounded-md text-left transition-all ${
                            bookingType === 'single' 
                              ? 'border-primary bg-primary/10' 
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid="single-booking-btn"
                        >
                          <User className="w-5 h-5 mb-2 text-primary" />
                          <div className="font-medium">Einzel-Anmeldung</div>
                          <div className="text-sm text-muted-foreground">€{course.price.toFixed(2)}</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setBookingType('couple')}
                          className={`p-4 border rounded-md text-left transition-all ${
                            bookingType === 'couple' 
                              ? 'border-primary bg-primary/10' 
                              : 'border-border hover:border-primary/50'
                          }`}
                          data-testid="couple-booking-btn"
                        >
                          <Users className="w-5 h-5 mb-2 text-primary" />
                          <div className="font-medium">Paar-Anmeldung</div>
                          <div className="text-sm text-muted-foreground">€{(course.price * 2).toFixed(2)}</div>
                        </button>
                      </div>
                    </div>

                    {bookingType === 'couple' && (
                      <div className="space-y-2 animate-fadeIn">
                        <Label className="form-label">Partner E-Mail</Label>
                        <Input
                          type="email"
                          placeholder="partner@email.de"
                          value={partnerEmail}
                          onChange={(e) => setPartnerEmail(e.target.value)}
                          className="bg-secondary border-border"
                          data-testid="partner-email"
                        />
                        <p className="text-xs text-muted-foreground">
                          Ihr Partner muss bereits registriert sein.
                        </p>
                      </div>
                    )}

                    <div className="pt-4 border-t border-border">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-muted-foreground">Gesamtpreis:</span>
                        <span className="text-2xl font-heading font-bold text-accent">
                          €{(bookingType === 'couple' ? course.price * 2 : course.price).toFixed(2)}
                        </span>
                      </div>
                      <Button 
                        onClick={handleBooking}
                        className="w-full btn-primary"
                        disabled={bookingLoading}
                        data-testid="confirm-booking-btn"
                      >
                        {bookingLoading ? 'Wird verarbeitet...' : 'Zur Zahlung'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Comments & Partner Search */}
          <div className="animate-fadeIn" style={{ animationDelay: '0.2s' }}>
            <Tabs defaultValue="discussion" className="w-full">
              <TabsList className="bg-secondary border border-border mb-6">
                <TabsTrigger value="discussion" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  <MessageCircle className="w-4 h-4" />
                  Diskussion
                </TabsTrigger>
                <TabsTrigger value="partner" className="gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                  <Heart className="w-4 h-4" />
                  Tanzpartnerbörse
                </TabsTrigger>
              </TabsList>

              <TabsContent value="discussion">
                <div className="space-y-6">
                  {/* Comment Form */}
                  <form onSubmit={handleComment} className="space-y-4">
                    <Textarea
                      placeholder="Schreiben Sie einen Kommentar..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      className="bg-secondary border-border min-h-24"
                      data-testid="comment-input"
                    />
                    <Button 
                      type="submit" 
                      className="btn-primary gap-2"
                      disabled={commentLoading || !newComment.trim()}
                      data-testid="submit-comment-btn"
                    >
                      <Send className="w-4 h-4" />
                      Kommentieren
                    </Button>
                  </form>

                  {/* Comments List */}
                  <div className="space-y-4">
                    {comments.filter(c => !c.is_partner_search).length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">
                        Noch keine Kommentare. Seien Sie der Erste!
                      </p>
                    ) : (
                      comments.filter(c => !c.is_partner_search).map(comment => (
                        <div key={comment.id} className="comment-item">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium">{comment.user_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(comment.created_at).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                          <p className="text-muted-foreground">{comment.content}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="partner">
                <div className="space-y-6">
                  {/* Partner Search Form */}
                  <form onSubmit={(e) => { setIsPartnerSearch(true); handleComment(e); }} className="space-y-4">
                    <Textarea
                      placeholder="Beschreiben Sie sich und was Sie suchen... z.B. 'Suche Tanzpartnerin für diesen Kurs. Ich bin Anfänger, 30 Jahre, flexibel bei Terminen.'"
                      value={newComment}
                      onChange={(e) => { setNewComment(e.target.value); setIsPartnerSearch(true); }}
                      className="bg-secondary border-border min-h-24"
                      data-testid="partner-search-input"
                    />
                    <Button 
                      type="submit" 
                      className="btn-primary gap-2"
                      disabled={commentLoading || !newComment.trim()}
                      data-testid="submit-partner-search-btn"
                    >
                      <Heart className="w-4 h-4" />
                      Partner-Gesuch veröffentlichen
                    </Button>
                  </form>

                  {/* Partner Search List */}
                  <div className="space-y-4">
                    {comments.filter(c => c.is_partner_search).length === 0 ? (
                      <p className="text-muted-foreground text-center py-8">
                        Noch keine Partner-Gesuche für diesen Kurs.
                      </p>
                    ) : (
                      comments.filter(c => c.is_partner_search).map(comment => (
                        <div key={comment.id} className="comment-item partner-search">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="partner-badge">
                              <Heart className="w-3 h-3" />
                              Sucht Partner
                            </span>
                            <span className="font-medium">{comment.user_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(comment.created_at).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                          <p className="text-foreground">{comment.content}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
